import { getAnalytics, logEvent as firebaseLogEvent, setUserProperties as firebaseSetUserProperties } from 'firebase/analytics';

/**
 * Firebase Analytics Service
 * 
 * Centralized analytics tracking for the application.
 * Provides methods to log events and set user properties.
 */
class AnalyticsService {
  constructor() {
    this.analytics = null;
    this.isInitialized = false;
  }

  /**
   * Initialize Firebase Analytics
   * Call this once in your app after Firebase is initialized
   */
  init(firebaseApp) {
    try {
      this.analytics = getAnalytics(firebaseApp);
      this.isInitialized = true;
      console.log('✅ Firebase Analytics initialized');
    } catch (error) {
      console.error('❌ Failed to initialize Firebase Analytics:', error);
      this.isInitialized = false;
    }
  }

  /**
   * Log a custom event
   * @param {string} eventName - Name of the event
   * @param {object} params - Event parameters
   */
  logEvent(eventName, params = {}) {
    if (!this.isInitialized) {
      console.warn('⚠️ Analytics not initialized. Skipping event:', eventName);
      return;
    }

    try {
      firebaseLogEvent(this.analytics, eventName, params);
      console.log('📊 Analytics Event:', eventName, params);
    } catch (error) {
      console.error('❌ Failed to log event:', eventName, error);
    }
  }

  /**
   * Set user properties
   * @param {object} properties - User properties to set
   */
  setUserProperties(properties) {
    if (!this.isInitialized) {
      console.warn('⚠️ Analytics not initialized. Skipping user properties');
      return;
    }

    try {
      firebaseSetUserProperties(this.analytics, properties);
      console.log('👤 User Properties Set:', properties);
    } catch (error) {
      console.error('❌ Failed to set user properties:', error);
    }
  }

  // ==================== AUTH EVENTS ====================

  /**
   * Track user sign up
   * @param {string} method - Sign up method (e.g., 'email')
   * @param {boolean} hasSchool - Whether user selected a school
   */
  trackSignUp(method = 'email', hasSchool = false) {
    this.logEvent('sign_up', {
      method: method,
      has_school: hasSchool
    });
  }

  /**
   * Track user login
   * @param {string} method - Login method (e.g., 'email')
   */
  trackLogin(method = 'email') {
    this.logEvent('login', {
      method: method
    });
  }

  /**
   * Set user properties after authentication
   * @param {object} user - User object from backend
   */
  setAuthUserProperties(user) {
    if (!user) return;

    this.setUserProperties({
      user_role: user.role || 'student',
      subscription_status: user.subscription_status || 'none',
      has_school: !!user.school,
      trial_used: user.trial_used || false
    });
  }
}

// Export singleton instance
const analytics = new AnalyticsService();
export default analytics;
